/**
 * P419.1 -- the three authoring surfaces, client side.
 *
 * P417, P418 and P419 each shipped an engine and no way to author what
 * it reads: declaring a tolerance window, composing a composite
 * scenario and recording a test run were all API acts.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type {
  CoverageReport,
  ScenarioComposition,
  ScenarioMemberRemoved,
  ScenarioTestRun,
} from "shared/api/types";

export const testRunKeys = {
  all: ["twin", "test-runs"] as const,
  list: (scenarioId?: string) => ["twin", "test-runs", scenarioId ?? "_all"] as const,
  coverage: (regime: string) => ["twin", "coverage", regime] as const,
  members: (scenarioId: string) => ["twin", "scenario-members", scenarioId] as const,
};

export function useTestRuns(
  scenarioId?: string,
): UseQueryResult<ScenarioTestRun[], ApiError> {
  return useQuery({
    queryKey: testRunKeys.list(scenarioId),
    queryFn: () =>
      api.get<ScenarioTestRun[]>(
        `/v1/twin/scenario-test-runs${
          scenarioId ? `?scenario_id=${encodeURIComponent(scenarioId)}` : ""
        }`,
      ),
  });
}

export function useCoverage(regime: string): UseQueryResult<CoverageReport, ApiError> {
  return useQuery({
    queryKey: testRunKeys.coverage(regime),
    queryFn: () =>
      api.get<CoverageReport>(
        `/v1/twin/critical-operation-coverage?regime_code=${encodeURIComponent(
          regime,
        )}`,
      ),
  });
}

export interface RecordRunInput {
  scenario_id: string;
  method: string;
  intensity: string;
  facilitator_email?: string;
  participants?: { kind: string; name: string }[];
  notes?: string;
  regime_code?: string;
}

export function useRecordRun(): UseMutationResult<
  ScenarioTestRun,
  ApiError,
  RecordRunInput
> {
  const qc = useQueryClient();
  return useMutation({
    // The caller shows its own message on failure, so the global sink
    // must not show a second one in different words (round 8 E1).
    meta: { handled: true },
    mutationFn: (body: RecordRunInput) =>
      api.post<ScenarioTestRun>("/v1/admin/twin/scenario-test-runs", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: testRunKeys.all });
      /* Round 17 D24 -- and the grid. A booked run is what makes a
         cell read "planned" (round 16 D22), and the grid sits under
         its own key: recording refreshed the run list and left the
         grid as it was, so the cell never showed planned until the
         run was executed and the grid read "tested" instead. */
      void qc.invalidateQueries({ queryKey: ["twin", "coverage"] });
    },
  });
}

export function useExecuteRun(): UseMutationResult<ScenarioTestRun, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      api.post<ScenarioTestRun>(
        `/v1/admin/twin/scenario-test-runs/${runId}/execute`,
        {},
      ),
    onSuccess: () => {
      // The coverage grid reads executed runs, so it is stale the
      // instant one lands. Both keys, named -- the C1 lesson about
      // invalidating a key that never matched.
      void qc.invalidateQueries({ queryKey: testRunKeys.all });
      void qc.invalidateQueries({ queryKey: ["twin", "coverage"] });
    },
  });
}

export function useScenarioMembers(
  scenarioId: string | null,
): UseQueryResult<ScenarioComposition[], ApiError> {
  return useQuery({
    queryKey: testRunKeys.members(scenarioId ?? "_none"),
    queryFn: () =>
      api.get<ScenarioComposition[]>(`/v1/scenarios/${scenarioId}/members`),
    enabled: Boolean(scenarioId),
  });
}

export function useAddMember(
  scenarioId: string,
): UseMutationResult<
  ScenarioComposition,
  ApiError,
  { member_scenario_id: string; offset_hours: number }
> {
  const qc = useQueryClient();
  return useMutation({
    meta: { handled: true },
    mutationFn: (body: { member_scenario_id: string; offset_hours: number }) =>
      api.post<ScenarioComposition>(`/v1/admin/scenarios/${scenarioId}/members`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: testRunKeys.members(scenarioId) });
      // Joining a composite takes the member OUT of the library, so the
      // scenario list is stale too.
      void qc.invalidateQueries({ queryKey: ["scenarios"] });
    },
  });
}

export function useRemoveMember(
  scenarioId: string,
): UseMutationResult<ScenarioMemberRemoved, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    // ROUND 15 D12 -- the removal answers with where the scenario still
    // sits, so the toast can say so instead of "returned to the library".
    mutationFn: (memberId: string) =>
      api.delete<ScenarioMemberRemoved>(
        `/v1/admin/scenarios/${scenarioId}/members/${memberId}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: testRunKeys.members(scenarioId) });
      void qc.invalidateQueries({ queryKey: ["scenarios"] });
    },
  });
}
